import { createSelector } from 'reselect';
import {
    filter, find, get, has, isEmpty, map, sortBy,
} from 'lodash';
import { CONSTANTS } from 'services/constants';
import { groupBy } from 'lodash/collection';

/**
 * Base Selectors
 */
const getMetaEntities = state => state.app.metaDatabase.hotels;
const getEntitySlug = (state, slug) => slug;

export const getHotels = state => state.app.database.hotels;
const getDestinations = state => state.app.database.destinations;
const getHotelPictures = state => state.app.database.hotelPictures;
const getHotelRooms = state => state.app.database.hotelRooms;
const getHotelPromotions = state => state.app.database.hotelPromotions;
const getAlternativeAttributes = state => state.app.database.alternativeAttributes;


/** * Meta Selector Builders */
export const buildMetaHotelsSelector = () => createSelector([getMetaEntities], (entities) =>
{
    if (!isEmpty(entities))
    {
        return entities[CONSTANTS.DATA.ALL_ID];
    }

    return {};
});

export const buildMetaHotelSelector = () => createSelector([getMetaEntities, getEntitySlug], (entities, slug) =>
{
    if (has(entities, slug))
    {
        return entities[slug];
    }

    return {};
});

export const buildMetaHotelsFeaturedSelector = () => createSelector([getMetaEntities], (entities) =>
{
    if (!isEmpty(entities))
    {
        return entities[CONSTANTS.DATA.ALL_ID];
    }

    return {};
});

export const buildMetaHotelsLatestSelector = () => createSelector([getMetaEntities], (entities) =>
{
    if (!isEmpty(entities))
    {
        return entities[CONSTANTS.DATA.ALL_ID];
    }

    return {};
});


/** * Selector Builders */
export const buildHotelsSelector = (includeRooms = true) => createSelector([getHotels, getDestinations, getHotelPictures, getHotelRooms, getHotelPromotions, getAlternativeAttributes], (entities, destinations, hotelPictures, hotelRooms, hotelPromotions, alternativeAttributes) =>
{
    if (!isEmpty(entities))
    {
        return mapHotels(entities, destinations, hotelPictures, includeRooms ? hotelRooms : [], hotelPromotions, alternativeAttributes);
    }

    return {};
});

export const buildHotelBySlugSelector = () => createSelector([getHotels, getEntitySlug, getDestinations, getHotelPictures, getHotelRooms, getHotelPromotions, getAlternativeAttributes], (entities, slug, destinations, hotelPictures, hotelRooms, hotelPromotions, alternativeAttributes) =>
{
    if (!isEmpty(entities))
    {
        const hotel = find(entities, entity => slug === entity.slug);

        if (!hotel) return {};

        return mapSingleHotel(
            hotel,
            get(destinations, hotel.destination_id, null),
            filter(hotelPictures, { hotel_id: hotel.id }),
            filter(hotelRooms, { hotel_id: hotel.id }),
            filter(hotelPromotions, { hotel_id: hotel.id }),
            filter(alternativeAttributes, { entity_id: hotel.id, entity_type: 'Hotel' }),
        );
    }

    return {};
});

export const buildHotelsFeaturedSelector = () => createSelector([getHotels, getDestinations, getHotelPictures, getHotelRooms, getHotelPromotions, getAlternativeAttributes], (entities, destinations, hotelPictures, hotelRooms, hotelPromotions, alternativeAttributes) =>
{
    if (!isEmpty(entities)) return mapHotels(entities, destinations, hotelPictures, hotelRooms, hotelPromotions, alternativeAttributes);

    return [];
});

export const buildHotelsLatestSelector = () => createSelector([getHotels, getDestinations, getHotelPictures, getHotelRooms, getHotelPromotions, getAlternativeAttributes], (entities, destinations, hotelPictures, hotelRooms, hotelPromotions, alternativeAttributes) =>
{
    if (!isEmpty(entities)) return mapHotels(entities, destinations, hotelPictures, hotelRooms, hotelPromotions, alternativeAttributes);

    return [];
});

function mapSingleHotel(hotel, destination, pictures, hotelRooms, hotelPromotions, alternativeAttributes)
{
    const sortedPictures = sortBy(pictures, ['position', 'id']);
    const mainPicture = find(sortedPictures, hotelPicture => hotelPicture.type === 'Gallery');

    return {
        ...hotel,
        mainPicture: get(mainPicture, 'path'),
        destination,
        hotelPictures: sortedPictures,
        hotelRooms,
        hotelValidPromotions: hotelPromotions,
        alternativeAttributes,
    };
}

function mapHotels(hotels, destinations, pictures, hotelRooms, hotelPromotions, alternativeAttributes)
{
    const picturesByHotel = groupBy(pictures, 'hotel_id');
    const roomsByHotel = groupBy(hotelRooms, 'hotel_id');
    const promosByHotel = groupBy(hotelPromotions, 'hotel_id');

    return map(hotels, (hotel) =>
    {
        const destination = get(destinations, hotel.destination_id, null);

        return mapSingleHotel(
            hotel,
            {
                ...destination,
                destination: get(destinations, destination.parent_id, null),
            },
            get(picturesByHotel, hotel.id, []),
            get(roomsByHotel, hotel.id, []),
            get(promosByHotel, hotel.id, []),
            filter(alternativeAttributes, { entity_id: hotel.id, entity_type: 'Hotel' }),
        );
    });
}
